import React, { useCallback, useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import config from '../config';

function Header() {
  const location = useLocation();
  const { user, logout } = useAuth();
  const [inbox, setInbox] = useState({
    stockRequestPending: 0,
    feedbackNew: 0,
    whatsappNew: 0,
    total: 0,
  });

  const isActive = (path) => location.pathname === path;
  const isLoanPageActive = isActive('/loan-management') || isActive('/loan-emi-dashboard');
  const isPawnPageActive = isActive('/pawn') || isActive('/pawn-app') || isActive('/pawn-analysis');
  const isPromotionsPageActive =
    isActive('/product-requests') ||
    isActive('/customer-feedback') ||
    isActive('/whatsapp-inbox') ||
    isActive('/featured-products') ||
    isActive('/hero-carousel') ||
    isActive('/buyback-campaign') ||
    isActive('/social-media');

  const isBusinessAnalysisActive =
    isActive('/loan-analysis') ||
    isActive('/advanced-business') ||
    isActive('/repair-analysis') ||
    isActive('/pawn-analysis');

  const isAdmin = user?.role === 'admin';

  const loadInbox = useCallback(async () => {
    try {
      const res = await axios.get(`${config.API_URL}/admin/inbox-counts`);
      setInbox(res.data || {});
    } catch {
      /* ignore poll errors */
    }
  }, []);

  useEffect(() => {
    loadInbox();
    const t = setInterval(loadInbox, 45000);
    return () => clearInterval(t);
  }, [loadInbox]);

  useEffect(() => {
    loadInbox();
  }, [location.pathname, loadInbox]);

  const promoBadge = inbox.total > 0 ? inbox.total : 0;

  return (
    <nav className="navbar navbar-expand-lg navbar-dark bg-dark">
      <div className="container-fluid">
        <Link className="navbar-brand" to="/">A2zTak Admin</Link>
        <button className="navbar-toggler" type="button" data-bs-toggle="collapse" data-bs-target="#navbarSupportedContent" aria-controls="navbarSupportedContent" aria-expanded="false" aria-label="Toggle navigation">
          <span className="navbar-toggler-icon"></span>
        </button>
        <div className="collapse navbar-collapse" id="navbarSupportedContent">
          <ul className="navbar-nav me-auto mb-2 mb-lg-0">
            <li className="nav-item">
              <Link className={`nav-link ${isActive('/products') ? 'active' : ''}`} to="/products"><i className="bi bi-box-seam me-1"></i> Products</Link>
            </li>

            {isAdmin && (
              <li className="nav-item">
                <Link className={`nav-link ${isActive('/inventory') ? 'active' : ''}`} to="/inventory"><i className="bi bi-person-lines-fill me-1"></i> Inventory</Link>
              </li>
            )}

            <li className="nav-item">
              <Link className={`nav-link ${isActive('/customers') ? 'active' : ''}`} to="/customers"><i className="bi bi-people me-1"></i> Customers</Link>
            </li>

            <li className="nav-item">
              <Link className={`nav-link ${isActive('/emi') || isActive('/emi-dashboard') ? 'active' : ''}`} to="/emi"><i className="bi bi-calendar-week me-1"></i> EMI Dashboard</Link>
            </li>

            {isAdmin && (
              <li className="nav-item">
                <Link className={`nav-link ${isActive('/repairs') ? 'active' : ''}`} to="/repairs"><i className="bi bi-tools me-1"></i> Repair</Link>
              </li>
            )}
            {isAdmin && (
              <li className="nav-item d-lg-none">
                <Link className={`nav-link ${isActive('/repair-app') ? 'active' : ''}`} to="/repair-app"><i className="bi bi-phone me-1"></i> Repair App</Link>
              </li>
            )}
            {isAdmin && (
              <li className="nav-item d-lg-none">
                <Link className={`nav-link ${isActive('/pawn-app') ? 'active' : ''}`} to="/pawn-app"><i className="bi bi-gem me-1"></i> Pawn App</Link>
              </li>
            )}

            {isAdmin && (
              <li className="nav-item dropdown">
                <a className={`nav-link dropdown-toggle ${isLoanPageActive ? 'active' : ''}`} href="#" role="button" data-bs-toggle="dropdown" aria-expanded="false">
                  <i className="bi bi-bank me-1"></i> Loan Business
                </a>
                <ul className="dropdown-menu">
                  <li><Link className={`dropdown-item ${isActive('/loan-management') ? 'active' : ''}`} to="/loan-management"><i className="bi bi-bank me-1"></i> Loan Management</Link></li>
                  <li><Link className={`dropdown-item ${isActive('/loan-emi-dashboard') ? 'active' : ''}`} to="/loan-emi-dashboard"><i className="bi bi-calendar-check me-1"></i> Loan EMI Dashboard</Link></li>
                </ul>
              </li>
            )}

            {isAdmin && (
              <li className="nav-item dropdown">
                <a className={`nav-link dropdown-toggle ${isPawnPageActive ? 'active' : ''}`} href="#" role="button" data-bs-toggle="dropdown" aria-expanded="false">
                  <i className="bi bi-gem me-1"></i> Pawn
                </a>
                <ul className="dropdown-menu">
                  <li><Link className={`dropdown-item ${isActive('/pawn-app') ? 'active' : ''}`} to="/pawn-app"><i className="bi bi-phone me-1"></i> Pawn App (mobile)</Link></li>
                  <li><Link className={`dropdown-item ${isActive('/pawn') ? 'active' : ''}`} to="/pawn"><i className="bi bi-box-seam me-1"></i> Pawn Counter</Link></li>
                  <li><Link className={`dropdown-item ${isActive('/pawn-analysis') ? 'active' : ''}`} to="/pawn-analysis"><i className="bi bi-graph-up me-1"></i> Pawn Analysis</Link></li>
                </ul>
              </li>
            )}

            <li className="nav-item dropdown">
              <a className={`nav-link dropdown-toggle ${isPromotionsPageActive ? 'active' : ''}`} href="#" role="button" data-bs-toggle="dropdown" aria-expanded="false">
                <span className="position-relative d-inline-block">
                  <i className="bi bi-megaphone me-1"></i> Promotions
                  {promoBadge > 0 && (
                    <span className="position-absolute top-0 start-100 translate-middle badge rounded-pill bg-danger" style={{ fontSize: '0.65rem' }}>
                      {promoBadge}
                    </span>
                  )}
                </span>
              </a>
              <ul className="dropdown-menu">
                <li>
                  <Link className={`dropdown-item d-flex justify-content-between align-items-center ${isActive('/product-requests') ? 'active' : ''}`} to="/product-requests">
                    <span><i className="bi bi-inbox me-1"></i> Stock Request</span>
                    {inbox.stockRequestPending > 0 && (
                      <span className="badge bg-danger">{inbox.stockRequestPending}</span>
                    )}
                  </Link>
                </li>
                <li>
                  <Link className={`dropdown-item d-flex justify-content-between align-items-center ${isActive('/customer-feedback') ? 'active' : ''}`} to="/customer-feedback">
                    <span><i className="bi bi-star-half me-1"></i> Customer Feedback</span>
                    {inbox.feedbackNew > 0 && (
                      <span className="badge bg-danger">{inbox.feedbackNew}</span>
                    )}
                  </Link>
                </li>
                <li>
                  <Link className={`dropdown-item d-flex justify-content-between align-items-center ${isActive('/whatsapp-inbox') ? 'active' : ''}`} to="/whatsapp-inbox">
                    <span><i className="bi bi-whatsapp me-1"></i> WhatsApp Messages</span>
                    {inbox.whatsappNew > 0 && (
                      <span className="badge bg-danger">{inbox.whatsappNew}</span>
                    )}
                  </Link>
                </li>
                <li><hr className="dropdown-divider" /></li>
                <li><Link className={`dropdown-item ${isActive('/featured-products') ? 'active' : ''}`} to="/featured-products"><i className="bi bi-star me-1"></i> Featured Products</Link></li>
                <li><Link className={`dropdown-item ${isActive('/hero-carousel') ? 'active' : ''}`} to="/hero-carousel"><i className="bi bi-images me-1"></i> Hero Carousel</Link></li>
                <li><Link className={`dropdown-item ${isActive('/buyback-campaign') ? 'active' : ''}`} to="/buyback-campaign"><i className="bi bi-arrow-repeat me-1"></i> Buyback Campaign</Link></li>
                <li><Link className={`dropdown-item ${isActive('/social-media') ? 'active' : ''}`} to="/social-media"><i className="bi bi-camera-reels me-1"></i> Social Media Reels</Link></li>
              </ul>
            </li>

            {isAdmin && (
              <li className="nav-item dropdown">
                <a className={`nav-link dropdown-toggle ${isBusinessAnalysisActive ? 'active' : ''}`} href="#" role="button" data-bs-toggle="dropdown" aria-expanded="false">
                  <i className="bi bi-speedometer2 me-1"></i> Business Analysis
                </a>
                <ul className="dropdown-menu">
                  <li><Link className={`dropdown-item ${isActive('/repair-analysis') ? 'active' : ''}`} to="/repair-analysis"><i className="bi bi-tools me-1"></i> Repair Business</Link></li>
                  <li><Link className={`dropdown-item ${isActive('/pawn-analysis') ? 'active' : ''}`} to="/pawn-analysis"><i className="bi bi-gem me-1"></i> Pawn Business</Link></li>
                  <li><Link className={`dropdown-item ${isActive('/loan-analysis') ? 'active' : ''}`} to="/loan-analysis"><i className="bi bi-graph-up-arrow me-1"></i> Loan Analysis</Link></li>
                  <li><Link className={`dropdown-item ${isActive('/advanced-business') ? 'active' : ''}`} to="/advanced-business"><i className="bi bi-lightning-charge me-1"></i> Advanced Analysis</Link></li>
                </ul>
              </li>
            )}
          </ul>
          <ul className="navbar-nav ms-auto">
            <li className="nav-item dropdown">
              <a className="nav-link dropdown-toggle" href="#" role="button" data-bs-toggle="dropdown" aria-expanded="false">
                <i className="bi bi-person-circle me-1"></i> {user?.name || 'User'}
                <span className="badge bg-success ms-2 text-uppercase">{user?.role}</span>
              </a>
              <ul className="dropdown-menu dropdown-menu-end">
                <li><span className="dropdown-item-text"><strong>{user?.name}</strong><br /><small className="text-muted">{user?.username}</small></span></li>
                <li><hr className="dropdown-divider" /></li>
                <li><button className="dropdown-item text-danger" onClick={logout}><i className="bi bi-box-arrow-right me-1"></i> Logout</button></li>
              </ul>
            </li>
          </ul>
        </div>
      </div>
    </nav>
  );
}

export default Header;
